/**
 * The document card: "Send the landlord the gas cert", answered with the
 * certificate itself, what it says, and the email waiting to take it
 * (docs/intelligent-chat-answers-plan.md, "Document card").
 *
 * 🚨 It is a VIEW OF A STAGED SEND, not a send of its own. `send_client_email`
 * with a `certificate_ref` stages an approval card in typed chat like every
 * other email; this block carries that card's id (`actionId`) and the
 * recipient, subject and words the card STORED (`engine_actions.payload.args`),
 * so the orange tap approves exactly that card through `decideFromChat`. There
 * is no second send path, and nothing here re-derives who it goes to: the
 * staged card already passed the recipient guard, and approving it replays the
 * stored call through the executor, which re-checks `send_emails` and
 * `send_gas_certs` and refuses a draft or superseded certificate.
 *
 * 🚨 No thumbnail. Nothing renders a picture of a certificate's first page, so
 * the card shows a document glyph and the facts read off the row — never a
 * drawn "page" that looks like the PDF and is not.
 *
 * 🚨 Every fact is OPTIONAL, because most certificates on production were
 * imported (CLAUDE.md #38): an imported record is a PDF with a stub payload,
 * so it has no outcome, no appliance list and no signer. A missing fact is
 * left off the card rather than guessed — the PDF is one tap away.
 *
 * Pure and directive-free (anti-pattern #18): the server builds it, the panel
 * and the Mucka Bar draw it, the oversight grader summarises it.
 */
import { CERT_TYPE_META, type GasCertType } from '@/lib/gas-certs/types';
import { jobDocPath } from '@/lib/jobs/document-refs';
import { calendarDate, ukDate } from '@/lib/utils/date-format';
import { ISSUED_CERT_STATUSES } from '@/lib/gas-certs/issued';

export type DocumentCardOutcome = 'pass' | 'pass_with_defects' | 'fail';

export interface DocumentCardSend {
  /** Who it goes to, named the way the staged card names them. */
  name: string;
  /** The address the staged card STORED — the one the approval replays. */
  email: string;
  subject: string;
  /** The body exactly as stored on the card (the signature is added on send). */
  words: string;
}

export interface DocumentCardBlock {
  kind: 'document-card';
  /** `engine_actions.id` of the staged send. Approving the card approves THAT row. */
  actionId: string;
  /** `gas_certificates.id`. */
  certId: string;
  /** "Landlord Gas Safety Record (CP12)". */
  title: string;
  /** "CP12". */
  shortLabel: string;
  serial: string | null;
  /** "6 October 2026" — the inspection or issue date, or null when the record carries none. */
  dateLabel: string | null;
  postcode: string | null;
  /** The engineer's declared result. Null on an imported record, or one with no verdict. */
  outcome: DocumentCardOutcome | null;
  /** Rows in the certificate's appliance grid. Null when it has no grid (imported). */
  applianceCount: number | null;
  /** The name on the engineer's signature, as the PDF prints it. */
  signedBy: string | null;
  /** The engineer named on the record, shown only when there is no signature name. */
  engineer: string | null;
  /** "6 Oct 2027". */
  dueAgainLabel: string | null;
  /** "7 Oct 2026" when the certificate has been emailed before. */
  lastSentLabel: string | null;
  /** In-app page that opens the PDF. A path, never a signed URL (stored replies). */
  openPath: string;
  send: DocumentCardSend;
}

/** What a Change tap says, as the owner's own words. */
export const DOCUMENT_CARD_CHANGE_TEXT = 'Send it to someone else instead';

/** The certificate row, as `document-card-data.ts` reads it. */
export interface DocumentCardCertRow {
  id: string;
  cert_type: string | null;
  serial: string | null;
  status: string | null;
  outcome: string | null;
  job_id: string | null;
  next_inspection_date: string | null;
  approved_at: string | null;
  sent_at: string | null;
  /** `data->appliances`: an array on a Mucka-written certificate, absent on an import. */
  appliances: unknown;
  /** `data->engineerSignature->>signedBy`. */
  signed_by: string | null;
  /** `data->>inspectionDate`, YYYY-MM-DD. */
  inspection_date: string | null;
  /** `data->>issuedDate`, written by the importers. */
  issued_date: string | null;
}

export interface DocumentCardInput {
  actionId: string;
  /** The staged card's stored args (`payload.args`). */
  args: Record<string, unknown>;
  cert: DocumentCardCertRow;
  postcode: string | null;
  /** `users.name` of `engineer_id`, for when the signature carries no name. */
  engineerName: string | null;
  /** The recipient's name as the staged card shows it. */
  recipientName: string;
}

const OUTCOMES: readonly DocumentCardOutcome[] = ['pass', 'pass_with_defects', 'fail'];

function text(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** A YYYY-MM-DD (or a timestamp's day) as a London calendar day. */
function day(value: string | null, options: Intl.DateTimeFormatOptions): string | null {
  if (!value) return null;
  const ymd = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
  try {
    return ukDate(ymd ? calendarDate(ymd) : value, options);
  } catch {
    return null;
  }
}

const LONG: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
const SHORT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

/**
 * The card, or null when the staged send is not a certificate email this card
 * can describe honestly — no stored recipient, no words, or a certificate that
 * is not issued (the approval would be refused, so the plain card stays).
 */
export function documentCardBlock(input: DocumentCardInput): DocumentCardBlock | null {
  const { cert, args } = input;
  const ref = text(args.certificate_ref);
  if (!ref || ref !== `cert:${cert.id}`) return null;
  // Only an issued certificate has a PDF that can go; the executor refuses the rest.
  if (!(ISSUED_CERT_STATUSES as readonly string[]).includes(cert.status ?? '')) return null;
  const email = text(args.to_email);
  const subject = text(args.subject);
  const words = typeof args.body === 'string' ? args.body : '';
  if (!email || !subject || !words.trim()) return null;

  const meta = CERT_TYPE_META[cert.cert_type as GasCertType];
  const signedBy = text(cert.signed_by);
  return {
    kind: 'document-card',
    actionId: input.actionId,
    certId: cert.id,
    title: meta?.label ?? 'Certificate',
    shortLabel: meta?.shortLabel ?? 'Certificate',
    serial: text(cert.serial),
    // The date the work was certified, never `created_at`: on an imported
    // record that is the day of the import, which would be a lie on a legal
    // document's card.
    dateLabel: day(text(cert.inspection_date) ?? text(cert.issued_date) ?? cert.approved_at, LONG),
    postcode: text(input.postcode),
    outcome: OUTCOMES.includes(cert.outcome as DocumentCardOutcome) ? cert.outcome as DocumentCardOutcome : null,
    applianceCount: Array.isArray(cert.appliances) ? cert.appliances.length : null,
    signedBy,
    engineer: signedBy ? null : text(input.engineerName),
    dueAgainLabel: day(cert.next_inspection_date, SHORT),
    lastSentLabel: day(cert.sent_at, SHORT),
    // The job's document viewer opens the PDF; a certificate on no job (a
    // property-only or imported record) opens on its own page.
    openPath: cert.job_id ? jobDocPath(cert.job_id, `cert:${cert.id}`) : `/gas-certs/${cert.id}`,
    send: { name: input.recipientName, email, subject, words },
  };
}

/** "Pass", "Pass with defects", "Fail". */
export function outcomeLabel(outcome: DocumentCardOutcome): string {
  return outcome === 'pass' ? 'Pass' : outcome === 'fail' ? 'Fail' : 'Pass with defects';
}

/** "3 appliances", "1 appliance". */
export function applianceLabel(n: number): string {
  return `${n} appliance${n === 1 ? '' : 's'}`;
}

/** The card's own sentence: "The Landlord Gas Safety Record (CP12) from 6 October 2026." */
export function documentCardSentence(block: DocumentCardBlock): string {
  return block.dateLabel ? `The ${block.title} from ${block.dateLabel}.` : `The ${block.title}.`;
}

/**
 * Appended to the tool RESULT so the model knows the card is on screen and
 * does not read it back. Names nothing the result did not already hold.
 */
export function documentCardResultNote(block: DocumentCardBlock): string {
  return `The certificate is on screen as a card with its result and due date, the email beneath it, and a "Send to ${block.send.name}" button — that button is the approval. `
    + 'Do not read the card out; say in a line what is ready and let them tap it.';
}

/**
 * The staged-card ids a reply's document cards already show, so the panel and
 * the bar can leave out the plain staged strip for those ids: the same card
 * drawn twice is two Send buttons for one email.
 */
export function documentCardActionIds(blocks: readonly unknown[] | null | undefined): Set<string> {
  const ids = new Set<string>();
  for (const b of blocks ?? []) {
    const block = b as { kind?: unknown; actionId?: unknown } | null;
    if (block?.kind === 'document-card' && typeof block.actionId === 'string') ids.add(block.actionId);
  }
  return ids;
}

/**
 * One line for the oversight grader: the kind of document, its state and what
 * is waiting. Nothing a customer could be identified by — no name, address,
 * postcode or serial.
 */
export function summariseDocumentCardBlock(block: DocumentCardBlock): string {
  const facts = [
    block.shortLabel,
    block.outcome ? outcomeLabel(block.outcome).toLowerCase() : 'no result on record',
    ...(block.applianceCount != null ? [applianceLabel(block.applianceCount)] : []),
    block.dueAgainLabel ? 'due date shown' : 'no due date',
  ];
  return `[document-card: ${facts.join(', ')}, email staged for approval, Send on the card]`;
}
